import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { taskAddChange, taskRemovedChange } from './websocket.action';
import { getUsersData, getTasksWaitingWSUpdate } from '../store/application.reducers';
import * as Api from 'utils/api/api';

export const MAX_ATTEMPTS = 5;
export const RECONNECT_BASE_DELAY_MS = 5000;
export const RECONNECT_MAX_DELAY_MS = 60000;
export const CONNECT_TIMEOUT_MS = 15000;

// crypto over Math.random purely to keep Sonar's PRNG hotspot off a path that has no security role
const jitterMs = () => window.crypto.getRandomValues(new Uint32Array(1))[0] % 1000;

export const WebSocketConnection = () => {
    const dispatch = useDispatch();
    const userData = useSelector(getUsersData);
    const tasksWaitingForWSResponse = useSelector(getTasksWaitingWSUpdate);

    const wsRef = useRef(null);
    const attemptsRef = useRef(0);
    const connectingRef = useRef(false);
    const retryTimerRef = useRef(null);
    const openTimeoutRef = useRef(null);
    const lastAttemptAtRef = useRef(0);
    const mountedRef = useRef(true);
    const connectRef = useRef(null);
    const userDataRef = useRef(userData);

    userDataRef.current = userData;

    const clearRetryTimer = useCallback(() => {
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    }, []);

    const clearOpenTimeout = useCallback(() => {
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    }, []);

    const detachAndClose = useCallback((ws) => {
        // detach first: closing a CONNECTING socket fires onclose(1006), which would schedule a reconnect
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
        ws.close();
    }, []);

    const closeLiveConnection = useCallback(() => {
        clearRetryTimer();
        clearOpenTimeout();
        const ws = wsRef.current;
        wsRef.current = null;
        if (ws)
            detachAndClose(ws);
    }, [clearRetryTimer, clearOpenTimeout, detachAndClose]);

    const parseWsMessage = useCallback((message) => {
        if (message.event === 'TASK_DELETED') {
            dispatch(taskRemovedChange(message.idTask));
        } else if (message.event === 'TASK_CHANGED') {
            if (message.details?.taskAssignmentStatus === "BLOCKED")
                dispatch(taskRemovedChange(message.idTask));
            else
                dispatch(taskAddChange(message.idTask));
        }
    }, [dispatch]);

    const parseWsMessages = useCallback((messages) => {
        if (Array.isArray(messages)) {
            let taskIds = [];
            messages.forEach(message => {
                if (message.event === 'TASK_CHANGED' && message.details?.taskAssignmentStatus !== "BLOCKED") {
                    taskIds.push(message.idTask);
                } else {
                    parseWsMessage(message);
                }
            });
            if (taskIds.length > 0)
                dispatch(taskAddChange(taskIds));
        } else {
            parseWsMessage(messages);
        }
    }, [dispatch, parseWsMessage]);

    const scheduleReconnect = useCallback(() => {
        clearRetryTimer();
        if (!mountedRef.current || attemptsRef.current >= MAX_ATTEMPTS)
            return;

        attemptsRef.current += 1;
        const backoff = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attemptsRef.current - 1), RECONNECT_MAX_DELAY_MS);
        // jitter so a tenant's tabs do not all reconnect on the same tick
        retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            connectRef.current?.();
        }, backoff + jitterMs());
    }, [clearRetryTimer]);

    const openLiveConnection = useCallback((token) => {
        let wsURL = window.ENVIRONMENT.TASKS_WEBSOCKETS + "?token=" + token;
        let ws = new window.WebSocket(wsURL);
        wsRef.current = ws;

        // a socket we have already replaced must not act on the live one
        const isCurrent = () => wsRef.current === ws;

        // a handshake that neither completes nor errors would otherwise wedge the connection forever
        clearOpenTimeout();
        openTimeoutRef.current = setTimeout(() => {
            openTimeoutRef.current = null;
            if (isCurrent() && ws.readyState !== window.WebSocket.OPEN) {
                wsRef.current = null;
                detachAndClose(ws);
                scheduleReconnect();
            }
        }, CONNECT_TIMEOUT_MS);

        ws.onopen = () => {
            if (!isCurrent())
                return;
            clearOpenTimeout();
            attemptsRef.current = 0;
        };

        ws.onmessage = (data) => {
            if (!isCurrent())
                return;
            let messages = JSON.parse(data.data);
            parseWsMessages(messages);
        };

        // a failed handshake fires error and then close(1006), so close is the single reconnect trigger
        ws.onclose = (event) => {
            if (!isCurrent())
                return;
            clearOpenTimeout();
            wsRef.current = null;
            if ((event?.code >= 1010 && event?.code <= 1014) || event?.code === 1006) {
                scheduleReconnect();
            }
        };
    }, [parseWsMessages, scheduleReconnect, clearOpenTimeout, detachAndClose]);

    const connect = useCallback(() => {
        connectingRef.current = true;
        lastAttemptAtRef.current = Date.now();
        Api.getWsToken().then(response => {
            connectingRef.current = false;
            if (!mountedRef.current)
                return;
            if (response?.webSocketToken)
                openLiveConnection(response.webSocketToken);
            else
                scheduleReconnect();
        }).catch(() => {
            connectingRef.current = false;
            if (mountedRef.current)
                scheduleReconnect();
        });
    }, [openLiveConnection, scheduleReconnect]);

    connectRef.current = connect;

    const hasLiveSocket = useCallback(() =>
        wsRef.current && wsRef.current.readyState !== window.WebSocket.CLOSED, []);

    /**
     * Single entry point for every reconnect trigger.
     * A user-initiated trigger (focus, tab becoming visible) may cut a long backoff short and
     * restart an exhausted budget, but only once per cooldown - otherwise coming back to the tab
     * during an outage storms the gateway.
     */
    const requestConnect = useCallback((userInitiated = false) => {
        // without a signed-in user the gateway only ever answers 401
        if (!userDataRef.current || hasLiveSocket() || connectingRef.current)
            return;

        const userOverride = userInitiated
            && Date.now() - lastAttemptAtRef.current >= RECONNECT_MAX_DELAY_MS;

        if (!userOverride && (retryTimerRef.current || attemptsRef.current >= MAX_ATTEMPTS))
            return;

        if (userOverride)
            attemptsRef.current = 0;

        clearRetryTimer();
        connect();
    }, [connect, hasLiveSocket, clearRetryTimer]);

    const checkWSConnection = useCallback(() => requestConnect(false), [requestConnect]);

    const handleVisibilityChange = useCallback(() => {
        if (!document.hidden) {
            requestConnect(true);
        }
    }, [requestConnect]);

    const onFocus = useCallback(() => {
        requestConnect(true);
    }, [requestConnect]);

    useEffect(() => {
        mountedRef.current = true;
        window.addEventListener("beforeunload", closeLiveConnection, false);
        document.addEventListener('visibilitychange', handleVisibilityChange, false);
        // window-level focus only - a document capture listener fires for every focusable element on the page
        window.addEventListener("focus", onFocus, false);

        checkWSConnection();

        return () => {
            mountedRef.current = false;
            window.removeEventListener("beforeunload", closeLiveConnection, false);
            document.removeEventListener("visibilitychange", handleVisibilityChange, false);
            window.removeEventListener("focus", onFocus, false);
            closeLiveConnection();
        };
    }, [closeLiveConnection, handleVisibilityChange, onFocus, checkWSConnection]);

    useEffect(() => {
        if (userData === null)
            closeLiveConnection();
        else
            checkWSConnection();
    }, [userData, closeLiveConnection, checkWSConnection]);

    useEffect(() => {
        checkWSConnection();
    }, [tasksWaitingForWSResponse, checkWSConnection]);

    return null;
};

export default WebSocketConnection;
